<template>
  <div class="flex h-screen overflow-hidden bg-white">
    <Modal title="New Booking" :open.sync="showNewBookingModal">
      <div class="p-6 space-y-4">
        <div v-for="form in portal.forms" :key="form.id">
          <router-link :to="{ name: 'Booking', params: { id: portal.forms[0].uuid } }" class="flex items-center justify-between p-4 font-medium text-gray-800 border rounded hover:border-gray-400 hover:no-underline hover:bg-gray-100">
            <div>
              {{ form.name }}
              <div v-if="form.description" class="mt-2 text-sm font-normal text-gray-500">
                {{ form.description }}
              </div>
            </div>
            <fa :icon="['far', 'chevron-right']" class="ml-4 text-gray-500" />
          </router-link>
        </div>
      </div>
    </Modal>

    <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. -->
    <div class="lg:hidden">
      <div class="fixed inset-0 z-40 flex" v-show="showMenu">
        <!-- Off-canvas menu overlay, show/hide based on off-canvas menu state. -->
        <transition
          enter-active-class="transition-opacity duration-300 ease-linear"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-300 ease-linear"
          leave-class="opacity-100"
          leave-to-class="opacity-0">
          <div class="fixed inset-0" v-if="showMenu" @click="showMenu = false">
            <div class="absolute inset-0 bg-gray-800 opacity-75"></div>
          </div>
        </transition>
        <!-- Off-canvas menu, show/hide based on off-canvas menu state. -->
        <transition
          enter-active-class="transition duration-300 ease-in-out transform"
          enter-class="-translate-x-full"
          enter-to-class="translate-x-0"
          leave-active-class="transition duration-300 ease-in-out transform"
          leave-class="translate-x-0"
          leave-to-class="-translate-x-full">
          <div tabindex="0" class="relative flex flex-col flex-1 w-full max-w-xs bg-white focus:outline-none" v-if="showMenu">
            <div class="absolute top-0 right-0 pt-2 -mr-12">
              <button type="button" class="flex items-center justify-center w-10 h-10 ml-1 rounded focus:outline-none focus:ring-accent" @click="showMenu = false">
                <span class="sr-only">Close sidebar</span>
                <fa :icon="['far', 'times']" class="text-xl text-white" />
              </button>
            </div>
            <div class="flex-1 h-0 overflow-y-auto">
              <div class="flex items-center flex-shrink-0 h-16 px-4 bg-white border-b">
                <img class="w-auto h-8" :src="portal.logo" :alt="portal.name">
              </div>
              <nav aria-label="Sidebar">
                <div class="p-4" v-if="portal.forms.length === 1">
                  <router-link :to="{ name: 'Booking', params: { id: portal.forms[0].uuid } }" class="w-full btn btn-accent">
                    New Booking
                  </router-link>
                </div>
                <div class="p-4" v-else-if="portal.forms.length > 1">
                  <button type="button" class="w-full btn btn-accent" @click="showNewBookingModal = true">
                    New Booking
                  </button>
                </div>
                <router-link v-for="link in links" :key="link.route" :to="{ name: link.route }" class="flex items-center w-full px-4 py-4 font-medium tracking-wide text-gray-700 border-transparent hover:no-underline group" active-class="text-gray-800 bg-gray-200 bg-opacity-50">
                  <fa :icon="['far', link.icon]" fixed-width class="mr-5 text-xl opacity-50 group-hover:opacity-75" />
                  {{ link.name }}
                </router-link>
              </nav>
            </div>
            <div class="flex flex-shrink-0 p-4 border-t border-gray-200 h-18">
              <div class="flex-shrink-0 block group">
                <div class="flex items-center">
                  <div>
                    <avatar :username="customer.name" :size="36" color="#fff" background-color="#718096" :custom-style="{ 'font-weight': 'bold' }" />
                  </div>
                  <div class="ml-3 leading-tight">
                    <p class="text-base font-medium text-gray-700">
                      {{ customer.name }}
                    </p>
                    <button class="text-sm font-medium text-accent hover:underline" @click.prevent="handleLogout">
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
        <div class="flex-shrink-0 w-14" aria-hidden="true">
          <!-- Force sidebar to shrink to fit close icon -->
        </div>
      </div>
    </div>

    <!-- Static sidebar for desktop -->
    <div class="hidden lg:flex lg:flex-shrink-0">
      <div class="flex flex-col w-64">
        <!-- Sidebar component, swap this element with another sidebar if you like -->
        <div class="flex flex-col flex-1 h-0 border-r border-gray-200 bg-gray-50">
          <div class="flex flex-col flex-1 overflow-y-auto">
            <div class="flex items-center flex-shrink-0 h-16 px-4 bg-white border-b">
              <img class="w-auto h-8" :src="portal.logo" :alt="portal.name">
            </div>
            <nav class="flex-1" aria-label="Sidebar">
              <div class="p-4" v-if="portal.forms.length === 1">
                <router-link :to="{ name: 'Booking', params: { id: portal.forms[0].uuid } }" class="w-full btn btn-accent">
                  New Booking
                </router-link>
              </div>
              <div class="p-4" v-else-if="portal.forms.length > 1">
                <button type="button" class="w-full btn btn-accent" @click="showNewBookingModal = true">
                  New Booking
                </button>
              </div>
              <router-link v-for="link in links" :key="link.route" :to="{ name: link.route }" class="flex items-center w-full px-4 py-4 font-medium tracking-wide text-gray-700 border-transparent hover:no-underline group" active-class="text-gray-800 bg-gray-200 bg-opacity-50">
                <fa :icon="['far', link.icon]" fixed-width class="mr-5 text-xl opacity-50 group-hover:opacity-75" />
                {{ link.name }}
              </router-link>
            </nav>
          </div>
          <div class="flex flex-shrink-0 p-4 border-t border-gray-200 h-17">
            <div class="flex-shrink-0 block w-full group">
              <div class="flex items-center">
                <div>
                  <avatar :username="customer.name" :size="36" color="#fff" background-color="#718096" :custom-style="{ 'font-weight': 'bold' }" />
                </div>
                <div class="ml-3 leading-none">
                  <p class="font-medium text-gray-700">
                    {{ customer.name }}
                  </p>
                  <button class="text-sm font-medium text-accent hover:underline" @click.prevent="handleLogout">
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col flex-1 min-w-0 overflow-hidden">
      <div class="lg:hidden">
        <div class="flex items-center h-16 justify-between bg-gray-50 border-b border-gray-200 px-4 py-1.5">
          <div>
            <img class="w-auto h-8" :src="portal.logo" :alt="portal.name">
          </div>
          <div>
            <button type="button" class="inline-flex items-center justify-center w-12 h-12 -mr-3 text-gray-500 rounded-md hover:text-gray-900 focus:outline-none focus:ring-accent" @click="showMenu = true">
              <span class="sr-only">Open sidebar</span>
              <fa :icon="['far', 'bars']" class="text-xl" />
            </button>
          </div>
        </div>
      </div>
      <div class="relative z-0 flex flex-1 overflow-auto">
        <router-view />
      </div>
    </div>
  </div>  
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import Avatar from 'vue-avatar'
import CustomerAuth from '@/views/customer/CustomerAuth'

export default {
  name: 'CustomerLayout',

  components: {
    Avatar,
    CustomerAuth
  },

  created () {
    if (this.$cookies.get('customer_token')) {
      this.syncCustomer()
    }
  },

  computed: {
    ...mapGetters(['account']),
    ...mapState('customer', ['customer']),
    ...mapState(['portal'])
  },

  data () {
    return {
      loading: true,
      tab: 'login',
      showMenu: false,
      showNewBookingModal: false,
      links: [
        { 
          route: 'CustomerBookingList',
          icon: 'clipboard',
          name: 'Bookings'
        },
        { 
          route: 'CustomerAddressList',
          icon: 'address-book',
          name: 'Address Book'
        },
        { 
          route: 'CustomerProfile',
          icon: 'user-circle',
          name: 'Profile'
        }
      ]
    }
  },

  methods: {
    ...mapActions('customer', ['syncCustomer', 'logout']),

    enter () {
      this.$router.push({ name: 'CustomerBookings' })
    },

    handleLogout () {
      this.logout()
      this.$router.push({ name: 'CustomerLogin' })
    }
  },

  watch: {
    '$route': {
      handler (newVal, oldVal) {
        this.showMenu = false
      }
    }
  }
}
</script>